import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { useHasProjectPermission } from '@/entities/user';
import { EmptyStateTitle } from '@/shared/ui';
import { libraryDescription, libraryTitle } from '../../library.constants';
import { useTranslation } from 'react-i18next';

interface Props {
  variant: 'empty' | 'no-results';
  /**
   * Fires when the user clicks the "Add artifacts" CTA in the true-empty
   * variant — same handler as the toolbar's "Add artifacts" button.
   */
  onAddArtifacts: () => void;
  /**
   * Fires when the user clicks "Clear filters" in the no-results variant —
   * resets search, kind, and folder filters back to the unfiltered list.
   */
  onClearFilters: () => void;
}

export function ArtifactsEmptyState(props: Props) {
  const { t } = useTranslation('projects');

  // Per AC, a viewer without upload permission gets heading + helper only —
  // no disabled button (the toolbar keeps its ScopedPermission treatment).
  const canUpload = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);

  if (props.variant === 'no-results') {
    return (
      <Empty role="status" data-testid="artifacts-no-results-state" className="flex-1">
        <EmptyHeader>
          <EmptyStateTitle>
            {t('projectDetail.artifactsEmptyState.noMatchingArtifacts')}
          </EmptyStateTitle>
          <EmptyDescription>
            {t('projectDetail.artifactsEmptyState.tryADifferentSearchOr')}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button variant="outline" onClick={props.onClearFilters}>
            {t('projectDetail.artifactsEmptyState.clearFilters')}
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  return (
    <Empty role="status" data-testid="library-empty-state" className="flex-1">
      <EmptyHeader>
        <EmptyStateTitle>{libraryTitle()}</EmptyStateTitle>
        <EmptyDescription>{libraryDescription()}</EmptyDescription>
      </EmptyHeader>
      {canUpload && (
        <EmptyContent>
          <Button size="default" onClick={props.onAddArtifacts}>
            <PlusIcon className="size-4" />
            {t('projectDetail.artifactsEmptyState.addArtifacts')}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}
